import { z } from 'zod'
import type { MetricEntry } from '../core/MonitoringClient'
import { LogFormatter } from '../formatters/LogFormatter'

export interface ValidationResult {
  isValid: boolean
  errors: string[]
  sanitizedMetric?: MetricEntry
}

export interface MetricValidatorConfig {
  maxMetadataKeys?: number
  maxMetadataSize?: number
  maxKeyLength?: number
  reservedKeys?: string[]
}

export class MetricValidator {
  static create(logFormatter?: LogFormatter, config?: MetricValidatorConfig): MetricValidator {
    return new MetricValidator(logFormatter ?? LogFormatter.create(), config)
  }

  private readonly config: Required<MetricValidatorConfig>

  constructor(
    private readonly logFormatter: LogFormatter,
    config?: MetricValidatorConfig
  ) {
    this.config = {
      maxMetadataKeys: config?.maxMetadataKeys ?? 20,
      maxMetadataSize: config?.maxMetadataSize ?? 5000,
      maxKeyLength: config?.maxKeyLength ?? 50,
      reservedKeys: config?.reservedKeys ?? ['timestamp', 'usecase', 'duration', 'success'],
    }
  }

  /**
   * Creates metadata schema with instance configuration
   */
  private createMetadataSchema() {
    return z.record(z.unknown()).superRefine((metadata, ctx) => {
      const keys = Object.keys(metadata)

      // Check number of keys
      if (keys.length > this.config.maxMetadataKeys) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `metadata cannot have more than ${this.config.maxMetadataKeys} keys`,
        })
      }

      // Check serialized size
      try {
        const serializedSize = this.logFormatter.serializeSafe(metadata).length
        if (serializedSize > this.config.maxMetadataSize) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `metadata size must be less than ${this.config.maxMetadataSize / 1000}KB when serialized`,
          })
        }
      } catch (error) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'metadata contains non-serializable values',
        })
      }

      // Validate individual keys
      keys.forEach(key => {
        if (key.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'metadata keys cannot be empty',
          })
          return
        }

        if (key.length > this.config.maxKeyLength) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `metadata key "${key}" must be less than ${this.config.maxKeyLength} characters`,
          })
          return
        }

        // Check for reserved keys
        if (this.config.reservedKeys.includes(key)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `metadata key "${key}" is reserved and cannot be used`,
          })
          return
        }

        // Validate value for circular references
        const value = metadata[key]
        if (value !== null && typeof value === 'object') {
          try {
            JSON.stringify(value)
          } catch (error) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `metadata value for key "${key}" contains circular references`,
            })
          }
        }
      })
    })
  }

  /**
   * Creates metric entry schema with instance configuration
   */
  private createMetricEntrySchema() {
    return z.object({
      id: z.string({
        required_error: 'id is required and must be a string',
        invalid_type_error: 'id is required and must be a string',
      }),
      usecase: z
        .string({
          required_error: 'usecase is required and must be a string',
          invalid_type_error: 'usecase is required and must be a string',
        })
        .min(1, 'usecase cannot be empty')
        .max(100, 'usecase must be less than 100 characters')
        .regex(
          /^[a-zA-Z][a-zA-Z0-9_]*$/,
          'usecase must start with a letter and contain only letters, numbers, and underscores'
        ),
      duration: z
        .number({
          required_error: 'duration is required and must be a number',
          invalid_type_error: 'duration is required and must be a number',
        })
        .min(0, 'duration must be a positive number')
        .max(1000000, 'duration must be less than 1,000,000ms (16.6 minutes)'),
      success: z.boolean({
        required_error: 'success is required and must be a boolean',
        invalid_type_error: 'success is required and must be a boolean',
      }),
      timestamp: z
        .number({ invalid_type_error: 'timestamp must be a number' })
        .min(0, 'timestamp must be positive')
        .optional(),
      metadata: this.createMetadataSchema().optional(),
    })
  }

  /**
   * Validates a MetricEntry and returns validation result
   */
  validate(metric: MetricEntry): ValidationResult {
    const schema = this.createMetricEntrySchema()
    const result = schema.safeParse(metric)

    if (result.success) {
      return {
        isValid: true,
        errors: [],
        sanitizedMetric: this.sanitizeMetric(result.data),
      }
    }

    const errors = result.error.errors.map(err => err.message)

    return {
      isValid: false,
      errors,
      sanitizedMetric: undefined,
    }
  }

  /**
   * Validates metadata object structure and content
   */
  validateMetadata(metadata: Record<string, unknown>): { errors: string[] } {
    const schema = this.createMetadataSchema()
    const result = schema.safeParse(metadata)

    if (result.success) {
      return { errors: [] }
    }

    return {
      errors: result.error.errors.map(err => err.message),
    }
  }

  /**
   * Sanitizes metadata by removing sensitive information and invalid values
   */
  sanitizeMetadata(metadata: Record<string, unknown>): Record<string, unknown> {
    return Object.entries(metadata).reduce(
      (sanitized, [key, value]) => {
        // Skip reserved keys
        if (this.config.reservedKeys.includes(key)) {
          return sanitized
        }

        // Sanitize sensitive keys
        const lowerKey = key.toLowerCase()
        if (
          lowerKey.includes('password') ||
          lowerKey.includes('token') ||
          lowerKey.includes('secret') ||
          lowerKey.includes('key')
        ) {
          return { ...sanitized, [key]: '***' }
        }

        // Handle different value types
        let sanitizedValue: unknown
        if (value === null || value === undefined) {
          sanitizedValue = value
        } else if (typeof value === 'string') {
          // Truncate long strings
          sanitizedValue = value.length > 200 ? `${value.substring(0, 197)}...` : value
        } else if (typeof value === 'number' || typeof value === 'boolean') {
          sanitizedValue = value
        } else if (typeof value === 'object') {
          try {
            // Ensure object is serializable
            JSON.stringify(value)
            sanitizedValue = value
          } catch {
            sanitizedValue = '[Non-serializable Object]'
          }
        } else {
          sanitizedValue = String(value)
        }

        return { ...sanitized, [key]: sanitizedValue }
      },
      {} as Record<string, unknown>
    )
  }

  /**
   * Sanitizes a complete metric entry
   */
  sanitizeMetric(metric: MetricEntry): MetricEntry {
    const sanitized: MetricEntry = {
      id: metric.id,
      usecase: metric.usecase,
      duration: Math.round(metric.duration * 100) / 100, // Round to 2 decimal places
      success: metric.success,
    }

    // Add timestamp if provided or generate one
    if (metric.timestamp) {
      sanitized.timestamp = metric.timestamp
    } else {
      sanitized.timestamp = Date.now()
    }

    // Sanitize metadata
    if (metric.metadata) {
      sanitized.metadata = this.sanitizeMetadata(metric.metadata)
    }

    return sanitized
  }

  /**
   * Quick validation for basic required fields (optimized for performance)
   */
  isValidBasic(metric: MetricEntry): boolean {
    const BasicMetricSchema = z.object({
      usecase: z.string().min(1),
      duration: z.number().min(0),
      success: z.boolean(),
    })

    return BasicMetricSchema.safeParse(metric).success
  }
}
